import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useState } from "react";
import { getReactHookFormNestedError } from "../../utils/errors";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import { getUniqueID } from "../../utils/numbers";
import { BORDER_RADIUS } from "../../utils/constants";
import PillTriggerButton, {
  PILL_TRIGGER_VARIANT,
} from "../Buttons/PillTriggerButton";
import Typography from "@mui/material/Typography";
/**
 * AutocompleteField component that supports both single and multiple (tags) selection modes.
 * If the prop 'multiple' is true, it will render as a tags input.
 *
 * Modes:
 * - 'form': Renders the traditional autocomplete with input field (default)
 * - 'text': Renders a clickable text that opens a menu with options
 */
const AutocompleteField = ({
  control,
  rules = {},
  errors = {},
  label = "",
  name = "",
  id = name,
  options = [],
  fullWidth = true,
  helperText,
  disabled = false,
  placeholder = "",
  sx,
  className = "",
  color = "primary",
  variant = "filled",
  InputProps = {},
  InputLabelProps = {},
  multiple = false,
  doTranslate = false,
  mode = "form",
  triggerVariant = PILL_TRIGGER_VARIANT.PLAIN,
  defaultPlaceholderText = "",
  onGetDisplayText: onGetDisplayTextProp,
  showEmptyOption = false,
  emptyOptionText = "",
  startIcon = null,
  sxText = {},
  filterOptions: filterOptionsProp,
  renderOption: renderOptionProp,
  disableCloseOnSelect = false,
  inputProps: inputPropsProp = {},
  size = "medium",
  disableClearable = false,
  onActionOptionSelect = () => {},
  onValueChange = () => {},
}) => {
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const onMenuClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const onMenuClose = () => {
    setAnchorEl(null);
  };

  const onMenuItemClick = (option, field) => {
    if (option === null) {
      field.onChange(multiple ? [] : null);
      onMenuClose();
      return;
    }

    if (multiple) {
      const currentValue = Array.isArray(field.value) ? field.value : [];
      const isSelected = currentValue.includes(option.value);

      if (isSelected) {
        // Remove from selection
        field.onChange(currentValue.filter((val) => val !== option.value));
      } else {
        // Add to selection
        field.onChange([...currentValue, option.value]);
      }
    } else {
      field.onChange(option.value);
      onMenuClose();
    }
  };

  const getDisplayText = (field) => {
    if (onGetDisplayTextProp) {
      const displayText = onGetDisplayTextProp(field, options);
      return displayText;
    }
    if (multiple) {
      const selectedOptions = options.filter(
        (opt) => Array.isArray(field.value) && field.value.includes(opt.value)
      );
      if (selectedOptions.length === 0) {
        return defaultPlaceholderText || placeholder || label;
      }
      return selectedOptions
        .map((opt) => (doTranslate ? t(opt.label) : opt.label))
        .join(", ");
    } else {
      const selectedOption = options.find((opt) => opt.value === field.value);
      if (!selectedOption) {
        return defaultPlaceholderText || placeholder || label;
      }
      return doTranslate ? t(selectedOption.label) : selectedOption.label;
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      id={name}
      rules={rules}
      render={({ field }) => {
        if (mode === "text") {
          const displayText = getDisplayText(field);

          return (
            <>
              <PillTriggerButton
                onClick={onMenuClick}
                disabled={disabled}
                isActive={isMenuOpen}
                text={displayText}
                startIcon={startIcon}
                variant={triggerVariant}
                sx={
                  triggerVariant === PILL_TRIGGER_VARIANT.PILL
                    ? sx
                    : { borderRadius: BORDER_RADIUS, p: 1, ...sx }
                }
                sxText={sxText}
              />
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={onMenuClose}
                MenuListProps={{
                  sx: {
                    maxHeight: 300,
                    overflow: "auto",
                  },
                }}
                PaperProps={{
                  sx: {
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                {showEmptyOption && (
                  <MenuItem onClick={() => onMenuItemClick(null, field)}>
                    {emptyOptionText}
                  </MenuItem>
                )}
                {options.map((option) => {
                  const isSelected = multiple
                    ? Array.isArray(field.value) &&
                      field.value.includes(option.value)
                    : field.value === option.value;

                  return (
                    <MenuItem
                      key={option.value}
                      onClick={() => onMenuItemClick(option, field)}
                      selected={isSelected}
                      sx={{
                        backgroundColor: isSelected
                          ? "action.selected"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      {doTranslate ? t(option.label) : option.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          );
        }

        // Original form mode
        // For multiple mode, value should be an array of ids
        // For single mode, value is a single id
        const value = multiple
          ? options.filter(
              (opt) =>
                !opt?.isSelectAll &&
                !opt?.isActionOption &&
                Array.isArray(field.value) &&
                field.value.includes(opt.value)
            )
          : options.find((opt) => opt.value === field.value) || null;

        return (
          <Autocomplete
            {...field}
            id={id}
            options={options}
            multiple={multiple}
            size={size}
            hasLabel={Boolean(label)}
            disableCloseOnSelect={disableCloseOnSelect}
            disableClearable={disableClearable}
            {...(filterOptionsProp ? { filterOptions: filterOptionsProp } : {})}
            {...(renderOptionProp ? { renderOption: renderOptionProp } : {})}
            getOptionLabel={(option) =>
              doTranslate ? t(option.label) : option.label || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={value}
            onChange={(_, newValue) => {
              if (multiple) {
                const list = newValue || [];
                const pickedSelectAll = list.some((item) => item?.isSelectAll);
                if (pickedSelectAll) {
                  const realOptions = options.filter((o) => !o?.isSelectAll);
                  field.onChange(realOptions.map((item) => item.value));
                  return;
                }
                field.onChange(
                  list
                    .filter((item) => !item?.isSelectAll)
                    .map((item) => item.value)
                );
              } else {
                if (newValue?.isActionOption) {
                  onActionOptionSelect(newValue);
                  return;
                }
                const nextValue = newValue ? newValue.value : null;
                field.onChange(nextValue);
                onValueChange(nextValue);
              }
            }}
            disabled={disabled}
            fullWidth={fullWidth}
            renderTags={
              multiple
                ? (tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        sx={{
                          backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                        }}
                        key={getUniqueID()}
                        label={doTranslate ? t(option.label) : option.label}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                : undefined
            }
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  label={label}
                  hiddenLabel={!label}
                  error={!!error}
                  placeholder={placeholder}
                  variant={variant}
                  color={color}
                  className={className}
                  size={size}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                    ...inputPropsProp,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    ...InputProps,
                  }}
                  InputLabelProps={InputLabelProps}
                  sx={{
                    ...sx,
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                />
                {(error?.message || helperText) && (
                  <Typography variant="caption" color="error.main">
                    {error?.message || helperText}
                  </Typography>
                )}
              </>
            )}
          />
        );
      }}
    />
  );
};

export default AutocompleteField;
